"use client";

import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, Send, Minimize2, Maximize2 } from "lucide-react";
import { aiChatAPI, authUtils } from "../../services/api";
import "./AIChatWidget.css";

const AIChatWidget = ({ isOpen, onClose, isMinimized, onMinimize, userTypeColor, userType }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  // Initialize messages from localStorage
  const [messages, setMessages] = useState(() => {
    try {
      const stored = localStorage.getItem("ai_chat_messages");
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert timestamp strings back to Date objects
        return parsed.map((msg) => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
        }));
      }
    } catch (error) {
      console.error("Error loading messages from localStorage:", error);
    }
    return [];
  });
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Use black text for veterinarians (teal background is hard to read with white text)
  const isVeterinarian = userType === "VETERINARIAN";
  const headerTextColor = isVeterinarian ? "#000000" : "#FFFFFF";
  const headerIconColor = isVeterinarian ? "#000000" : "#FFFFFF";

  // Persist messages to localStorage whenever they change
  useEffect(() => {
    try {
      // Convert Date objects to ISO strings for storage
      const messagesToStore = messages.map((msg) => ({
        ...msg,
        timestamp: msg.timestamp.toISOString(),
      }));
      localStorage.setItem("ai_chat_messages", JSON.stringify(messagesToStore));
    } catch (error) {
      console.error("Error saving messages to localStorage:", error);
    }
  }, [messages]);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Focus input when widget opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Scroll to bottom when widget opens or is restored from minimized state
  useEffect(() => {
    if (isOpen && !isMinimized) {
      // Use a small delay to ensure the DOM is fully rendered
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 150);
    }
  }, [isOpen, isMinimized]);

  // Handle sending messages
  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage.trim(),
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const messageText = inputMessage.trim();
    setInputMessage("");
    // Refocus input immediately after clearing
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
    setIsLoading(true);

    try {
      // Get yana_id from user data if available, otherwise use default (5)
      const userData = authUtils.getCurrentUser();
      const yanaId = userData?.yana_id || 5;

      const response = await aiChatAPI.sendMessage(messageText, yanaId);
      const responseData = response.data;

      // Extract the answer from the response
      const aiAnswer = responseData?.answer || "I'm sorry, I couldn't process your request. Please try again.";

      const aiMessage = {
        id: Date.now() + 1,
        text: aiAnswer,
        sender: "ai",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error("Error sending message to AI:", error);

      // Show error message to user
      const errorMessage = {
        id: Date.now() + 1,
        text: error.response?.data?.message || error.message || "Sorry, there was an error processing your message. Please try again.",
        sender: "ai",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      // Refocus input after message is processed
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };

  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`ai-chat-widget ${isMinimized ? "minimized" : ""}`} dir={isRTL ? "rtl" : "ltr"}>
      {/* Header */}
      <div
        className="ai-chat-header"
        onClick={() => isMinimized && onMinimize()}
        style={{
          background: `linear-gradient(135deg, ${userTypeColor} 0%, ${userTypeColor}dd 100%)`,
          cursor: isMinimized ? "pointer" : "default"
        }}
      >
        <div className="ai-chat-header-content">
          <div
            className="ai-chat-header-icon"
            onClick={(e) => {
              if (isMinimized) {
                e.stopPropagation();
                onMinimize();
              }
            }}
            style={{
              color: headerIconColor,
              background: "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: isMinimized ? "pointer" : "default"
            }}
          >
            <img
              src="/images/yana-logo-nobg.png"
              alt="AI Assistant"
              style={{
                width: "32px",
                height: "32px",
                objectFit: "contain"
              }}
            />
          </div>
          <div className="ai-chat-header-text" style={{ color: headerTextColor }}>
            <h3>{t("aiChat.title") || "AI Assistant"}</h3>
            <p>{t("aiChat.subtitle") || "How can I help you today?"}</p>
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button
            onClick={onMinimize}
            className="ai-chat-minimize-btn"
            aria-label={isMinimized ? (t("aiChat.restore") || "Restore") : (t("aiChat.minimize") || "Minimize")}
            style={{
              background: isVeterinarian ? "rgba(0, 0, 0, 0.2)" : "rgba(255, 255, 255, 0.2)",
              color: headerIconColor
            }}
          >
            {isMinimized ? <Maximize2 size={20} /> : <Minimize2 size={20} />}
          </button>
          <button
            onClick={onClose}
            className="ai-chat-close-btn"
            aria-label={t("aiChat.close") || "Close"}
            style={{
              background: isVeterinarian ? "rgba(0, 0, 0, 0.2)" : "rgba(255, 255, 255, 0.2)",
              color: headerIconColor
            }}
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Messages Container */}
      {!isMinimized && (
        <>
          <div className="ai-chat-messages">
            {messages.length === 0 ? (
              <div className="ai-chat-empty">
                <img
                  src="/images/yana-logo-nobg.png"
                  alt="AI Assistant"
                  style={{
                    width: "48px",
                    height: "48px",
                    opacity: 0.3,
                    marginBottom: "16px",
                    objectFit: "contain"
                  }}
                />
                <p>{t("aiChat.emptyMessage") || "Start a conversation with the AI assistant"}</p>
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`ai-chat-message ${message.sender === "user" ? "user-message" : "ai-message"}`}
                >
                  <div className="ai-chat-message-content">
                    {message.text}
                  </div>
                  <div className="ai-chat-message-time">
                    {new Date(message.timestamp).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
              ))
            )}
            {isLoading && (
              <div className="ai-chat-message ai-message">
                <div className="ai-chat-message-content">
                  <div className="ai-chat-typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="ai-chat-input-container">
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t("aiChat.inputPlaceholder") || "Type your message..."}
              className="ai-chat-input"
              disabled={isLoading}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading}
              className="ai-chat-send-btn"
              style={{
                background: userTypeColor,
                opacity: !inputMessage.trim() || isLoading ? 0.5 : 1,
              }}
            >
              <Send size={18} />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AIChatWidget;

